import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, AlertTriangle, Eye, Reply, Send, Paperclip, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { VisibilityBadges, VisibilityControls } from "@/components/common/Visibility";

const fmt = (d) => (d ? new Date(d).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const API_BASE = process.env.REACT_APP_BACKEND_URL;

function Chip({ tone, icon: I, children, testId, title }) {
  const tones = { green: "bg-emerald-50 text-emerald-700 border-emerald-200", red: "bg-rose-50 text-rose-700 border-rose-200",
    blue: "bg-sky-50 text-sky-700 border-sky-200", slate: "bg-slate-50 text-slate-600 border-slate-200", brand: "bg-brand/10 text-brand border-brand/30" };
  return <span title={title} data-testid={testId} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${tones[tone]}`}><I className="w-3 h-3" />{children}</span>;
}

// Outbound: Sent → Delivered / Delivery Failed → Read receipt → Replied (strongest). Never shows "Not opened".
export function EmailStatusLine({ c }) {
  if (!c) return null;
  if (c.direction === "inbound") {
    return <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`email-status-${c.id}`}>
      <Chip tone="brand" icon={Reply} testId={`email-inbound-${c.id}`}>Received {fmt(c.received_at)}</Chip>
      {c.attachments?.length > 0 && <Chip tone="slate" icon={Paperclip}>{c.attachments.length} attachment{c.attachments.length > 1 ? "s" : ""}</Chip>}
    </div>;
  }
  if (c.status !== "sent") return null;
  return <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`email-status-${c.id}`}>
    {c.replied_at && <Chip tone="green" icon={Reply} testId={`email-replied-${c.id}`}>Replied {fmt(c.replied_at)}{c.reply_count > 1 ? ` · ${c.reply_count} replies` : ""}</Chip>}
    {c.delivery_status === "failed"
      ? <Chip tone="red" icon={AlertTriangle} testId={`email-failed-${c.id}`} title={c.delivery_failure_reason || ""}>Delivery failed {fmt(c.delivery_failed_at)}</Chip>
      : c.delivered_at ? <Chip tone="blue" icon={CheckCircle2} testId={`email-delivered-${c.id}`}>Delivered {fmt(c.delivered_at)}</Chip>
        : <Chip tone="slate" icon={Send} testId={`email-sent-${c.id}`}>Sent {fmt(c.sent_at || c.created_at)}</Chip>}
    {c.read_receipt_at && <Chip tone="slate" icon={Eye} testId={`email-read-${c.id}`}>Read receipt {fmt(c.read_receipt_at)}</Chip>}
    {c.auto_replies > 0 && !c.replied_at && <span className="text-[11px] text-slate-400" data-testid={`email-auto-${c.id}`}>Automatic reply received</span>}
  </div>;
}

export function ViewConversationButton({ commId }) {
  const [open, setOpen] = useState(false);
  return <>
    <button onClick={() => setOpen(true)} data-testid={`view-email-conversation-${commId}`} className="mt-1 text-xs text-brand font-medium hover:underline inline-flex items-center gap-1">
      <MessagesSquare className="w-3.5 h-3.5" />View conversation</button>
    {open && <ConversationDialog commId={commId} onClose={() => setOpen(false)} />}
  </>;
}

function Bubble({ m, onVis }) {
  const inbound = m.direction === "inbound";
  const from = inbound ? (m.from_name || m.from_email) : (m.sender_name || m.sender_email);
  const to = inbound ? (m.mailbox_email || "you") : (m.to_name || m.to_email);
  return (
    <div className={`flex ${inbound ? "justify-start" : "justify-end"}`} data-testid={`thread-msg-${m.id}`}>
      <div className={`max-w-[85%] rounded-xl border px-4 py-3 ${inbound ? "bg-white border-slate-200" : "bg-sky-50/60 border-sky-100"}`}>
        <p className="text-xs font-semibold text-slate-700">{from} <span className="text-slate-400 font-normal">→ {to}</span></p>
        <p className="text-[11px] text-slate-400">{fmt(m.received_at || m.sent_at || m.created_at)} · {m.subject || "(no subject)"}</p>
        <div className="mt-2 text-sm text-slate-700 [&_p]:my-1.5 break-words" dangerouslySetInnerHTML={{ __html: m.bodyhtml || "" }} />
        {m.attachments?.map((a) => (
          <a key={a.id} href={`${API_BASE}/api/communications/${m.id}/attachments/${a.id}`} data-testid={`thread-att-${a.id}`}
            onClick={async (e) => { e.preventDefault(); try { const r = await api.get(`/communications/${m.id}/attachments/${a.id}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const l = document.createElement("a"); l.href = u; l.download = a.name; l.click(); URL.revokeObjectURL(u); } catch (er) { toast.error(apiError(er)); } }}
            className="mt-2 mr-2 inline-flex items-center gap-1 text-xs text-brand hover:underline"><Paperclip className="w-3 h-3" />{a.name} ({Math.max(1, Math.round((a.size || 0) / 1024))} KB)</a>
        ))}
        <EmailStatusLine c={m} />
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid={`thread-vis-${m.id}`}>
          {m.can_edit_visibility
            ? <VisibilityControls internal={m.internal_visibility || "group"} portal={m.portal_visibility || "internal_only"}
                onInternal={(v) => onVis(m, { internal_visibility: v })} onPortal={(v) => onVis(m, { portal_visibility: v })} testPrefix={`thread-vis-${m.id}`} />
            : <VisibilityBadges item={m} testId={`thread-badges-${m.id}`} />}
        </div>
      </div>
    </div>
  );
}

export function ConversationDialog({ commId, onClose }) {
  const [items, setItems] = useState(null);
  const load = () => api.get(`/communications/${commId}/thread`).then((r) => setItems(r.data.items)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); }, [commId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onVis = async (m, patch) => { try { await api.patch(`/communications/${m.id}/visibility`, patch); toast.success("Visibility updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl" data-testid="email-conversation-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>Email conversation</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-auto pr-1 bg-slate-50 rounded-lg p-3">
          {items === null ? <p className="text-sm text-slate-400">Loading…</p> : items.map((m) => <Bubble key={m.id} m={m} onVis={onVis} />)}
        </div>
        {items?.length > 0 && <ReplyBox commId={commId} onSent={load} />}
      </DialogContent>
    </Dialog>
  );
}

function ReplyBox({ commId, onSent }) {
  const [text, setText] = useState("");
  const [rr, setRr] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const html = text.split(/\n{2,}/).map((p) => `<p>${p.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\n/g, "<br>")}</p>`).join("");
      await api.post(`/communications/${commId}/reply`, { bodyhtml: html, request_read_receipt: rr });
      toast.success("Reply sent from your mailbox"); setText(""); setRr(false); onSent();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="email-reply-box">
      <Textarea rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply… your saved signature is added automatically" data-testid="email-reply-input" />
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" className="h-4 w-4 accent-brand" checked={rr} onChange={(e) => setRr(e.target.checked)} data-testid="email-reply-read-receipt" />Request a read receipt</label>
        <Button className="ml-auto bg-brand hover:bg-brand-strong text-white" onClick={send} disabled={busy || !text.trim()} data-testid="email-reply-send"><Reply className="w-4 h-4 mr-1.5" />{busy ? "Sending…" : "Reply"}</Button>
      </div>
      <p className="text-[11px] text-slate-400">Sends from your own connected Microsoft 365 mailbox. Replies to messages in your mailbox stay in the same Outlook conversation.</p>
    </div>
  );
}
